import { apiRequest } from '../lib/api-client';

/** Typed access to the inbox and WhatsApp endpoints (phases 17 and 18). */

export type ConversationStatus = 'OPEN' | 'PENDING' | 'SNOOZED' | 'RESOLVED';
export type DeliveryStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
export type ChannelKind =
  'WHATSAPP' | 'SMS' | 'EMAIL' | 'WEBCHAT' | 'INSTAGRAM' | 'FACEBOOK' | 'TELEGRAM';

export interface SlaView {
  firstResponse: string;
  resolution: string;
  firstResponseDueAt?: string | null;
  minutesToFirstResponse?: number | null;
}

/** A message found by searching, with enough of its conversation to be worth showing. */
export interface FoundMessage {
  id: string;
  conversationId: string;
  direction: string;
  body: string;
  createdAt: string;
  peerName: string | null;
  /** The provider's picture for the customer. Short-lived and may be null. */
  peerAvatarUrl?: string | null;
  peerAddress: string;
  subject: string | null;
  channelKind: string;
}

/** Searches message bodies; the conversation list searches names, addresses and subjects. */
export function searchInbox(query: string, limit = 20): Promise<{ messages: FoundMessage[] }> {
  return apiRequest(`/v1/inbox/messages/search?q=${encodeURIComponent(query)}&limit=${limit}`);
}

export interface ConversationSummary {
  id: string;
  channelId: string;
  contactId: string | null;
  peerAddress: string;
  peerName: string | null;
  /** The provider's picture for the customer. Short-lived and may be null. */
  peerAvatarUrl?: string | null;
  /** The stored copy of that picture, asked for as a signed link when it is drawn (Phase 93). */
  peerAvatarFileId?: string | null;
  subject: string | null;
  status: ConversationStatus;
  priority: number;
  assigneeId: string | null;
  queueId: string | null;
  unreadCount: number;
  tags: string[];
  /** Who is answering: the bot, nobody yet (handoff asked), or a person. */
  handling: 'BOT' | 'HANDOFF_REQUESTED' | 'HUMAN';
  handoffReason: string | null;
  lastMessageAt: string | null;
  sla: SlaView;
  createdAt: string;
}

export interface MessageView {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  authorType: 'CONTACT' | 'USER' | 'AI_AGENT' | 'SYSTEM';
  authorId: string | null;
  body: string;
  media: unknown[];
  isPrivate: boolean;
  status: DeliveryStatus;
  failureReason: string | null;
  createdAt: string;
  /** The buttons or list this message offered, drawn as options rather than as a paragraph. */
  interactive?: InteractiveMessage;
  /** The id of the option the customer tapped, when this message is an answer to one. */
  replyToOption?: string | null;
  /** When this message was withdrawn from the customer's chat (Phase 107). */
  deletedAt?: string;
  /** Which AI agent wrote this, when one did (Phase 110). */
  authorName?: string;
  /** The message this one replies to, as much of it as the quote shows (Phase 106). */
  repliedTo?: {
    id: string;
    direction: 'INBOUND' | 'OUTBOUND';
    authorType: 'CONTACT' | 'USER' | 'AI_AGENT' | 'SYSTEM';
    excerpt: string;
    hasMedia: boolean;
  } | null;
}

/** Reply buttons and lists — WhatsApp's interactive messages (context.md §53–§56). */

export interface ReplyButton {
  id: string;
  title: string;
}

export interface ListRow {
  id: string;
  title: string;
  description?: string;
}

export type InteractiveMessage =
  | { kind: 'BUTTONS'; body: string; header?: string; footer?: string; buttons: ReplyButton[] }
  | {
      kind: 'LIST';
      body: string;
      header?: string;
      footer?: string;
      buttonLabel: string;
      sections: { title: string; rows: ListRow[] }[];
    };

/** Meta's limits, mirrored so the form can stop somebody before the server has to. */
export const INTERACTIVE_LIMITS = {
  body: 1024,
  header: 60,
  footer: 60,
  buttonTitle: 20,
  buttons: 3,
  listButtonLabel: 20,
  /** Across every section, not per section. */
  rows: 10,
  rowTitle: 24,
  rowDescription: 72,
} as const;

export interface ThreadEvent {
  kind: string;
  actorType: string;
  actorId: string | null;
  data: unknown;
  at: string;
}

export interface ConversationDetail extends ConversationSummary {
  messages: MessageView[];
  events: ThreadEvent[];
}

export interface InboxFilters {
  view?: 'mine' | 'unassigned' | 'queue' | 'all';
  queueId?: string;
  status?: ConversationStatus;
  channelId?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

export async function listConversations(
  filters: InboxFilters,
): Promise<{ conversations: ConversationSummary[]; next_cursor: string | null }> {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }

  return apiRequest(`/v1/inbox/conversations?${params.toString()}`);
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  return apiRequest(`/v1/inbox/conversations/${id}`);
}

/** An attachment, already uploaded. The message carries its id, never its bytes. */
export interface OutboundAttachment {
  fileId: string;
  filename?: string;
  mimeType?: string;
  /** A recording rather than an audio file: it arrives as a voice note. */
  voice?: boolean;
}

/**
 * Asks WhatsApp to take a message off the customer's phone (Phase 107).
 *
 * The row stays here: the thread is the record of what was said. WhatsApp may refuse — its own rule
 * about how long a message may be withdrawn — and that refusal arrives as an error with its words.
 */
/**
 * Turns one voice note into words (Phase 110).
 *
 * The index names which attachment: a message can carry two recordings, and a caption under the
 * wrong one is worse than none.
 */
export async function transcribeAttachment(
  conversationId: string,
  messageId: string,
  index: number,
): Promise<MessageView> {
  return apiRequest(
    `/v1/inbox/conversations/${conversationId}/messages/${messageId}/attachments/${index}/transcribe`,
    { method: 'POST' },
  );
}

export async function deleteForCustomer(
  conversationId: string,
  messageId: string,
): Promise<MessageView> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/messages/${messageId}/delete`, {
    method: 'POST',
  });
}

/** A reply written now and sent later (Phase 115). */
export interface ScheduledMessage {
  id: string;
  conversationId: string;
  body: string;
  media: unknown[];
  sendAt: string;
  cancelOnReply: boolean;
  status: 'SCHEDULED' | 'SENT' | 'CANCELLED' | 'FAILED';
  detail: string | null;
  createdAt: string;
}

export function listScheduled(conversationId: string): Promise<{ scheduled: ScheduledMessage[] }> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/scheduled`);
}

export function scheduleMessage(
  conversationId: string,
  input: { body: string; media?: OutboundAttachment[]; sendAt: string; cancelOnReply?: boolean },
): Promise<ScheduledMessage> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/scheduled`, {
    method: 'POST',
    body: input,
  });
}

export function cancelScheduled(id: string): Promise<void> {
  return apiRequest(`/v1/inbox/scheduled/${id}`, { method: 'DELETE' });
}

/**
 * What this conversation is about, written by a model on request (Phase 116).
 *
 * `ThreadSummary` rather than `ConversationSummary`, which this file already uses for a row in the
 * conversation list — a different kind of summary, and one somebody would eventually confuse.
 */
export interface ThreadSummary {
  text: string | null;
  generatedAt: string | null;
  throughMessageId: string | null;
  model: string | null;
  /** True when the thread has moved on since — including when there is no summary at all. */
  stale: boolean;
}

/** Reads what is stored. Never calls a model, so drawing the screen costs nothing. */
export function getSummary(conversationId: string): Promise<ThreadSummary> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/summary`);
}

/** The button. This is the call that spends. */
export function summarise(conversationId: string): Promise<ThreadSummary> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/summary`, { method: 'POST' });
}

/** An answer the desk sends every day, already filled in for this conversation (Phase 114). */
export interface QuickReply {
  id: string;
  shortcut: string;
  title: string;
  body: string;
  teamId: string | null;
  usedCount: number;
}

export function listQuickReplies(
  conversationId?: string,
): Promise<{ quick_replies: QuickReply[] }> {
  return apiRequest(
    conversationId
      ? `/v1/inbox/quick-replies?conversationId=${conversationId}`
      : '/v1/inbox/quick-replies',
  );
}

/** Counted after the message is sent, so the list orders itself by what the desk leans on. */
export function noteQuickReplyUse(id: string): Promise<void> {
  return apiRequest(`/v1/inbox/quick-replies/${id}/used`, { method: 'POST' });
}

export async function sendReply(
  id: string,
  input: {
    body: string;
    isPrivate?: boolean;
    media?: OutboundAttachment[];
    /** The message being replied to, so WhatsApp shows it quoted (Phase 106). */
    repliedToId?: string | null;
    /** Sign this one, or not, over whatever the desk decided (Phase 113). */
    sign?: boolean;
  },
): Promise<MessageView> {
  return apiRequest(`/v1/inbox/conversations/${id}/messages`, { method: 'POST', body: input });
}

/** Writes to somebody who has not written first; reuses the thread when there already is one. */
export async function startConversation(input: {
  channelId: string;
  to: string;
  body: string;
  media?: OutboundAttachment[];
}): Promise<{ conversationId: string; messageId: string }> {
  return apiRequest('/v1/inbox/conversations', { method: 'POST', body: input });
}

export type Availability = 'AVAILABLE' | 'BUSY' | 'AWAY';

export interface TeamMemberAvailability {
  userId: string;
  name: string;
  availability: Availability;
  /** Whether they have the product open right now. Not the same as what they said. */
  connected: boolean;
  changedAt: string | null;
}

export function teamAvailability(): Promise<{ members: TeamMemberAvailability[] }> {
  return apiRequest('/v1/inbox/availability');
}

export function setMyAvailability(
  availability: Availability,
): Promise<{ availability: Availability }> {
  return apiRequest('/v1/inbox/availability', { method: 'PUT', body: { availability } });
}

export async function setConversationStatus(
  id: string,
  input: { status: ConversationStatus; snoozeUntil?: string },
): Promise<ConversationSummary> {
  return apiRequest(`/v1/inbox/conversations/${id}/status`, { method: 'POST', body: input });
}

export async function assignConversation(
  id: string,
  assigneeId: string | null,
): Promise<ConversationSummary> {
  return apiRequest(`/v1/inbox/conversations/${id}/assign`, {
    method: 'POST',
    body: { assigneeId },
  });
}

// ---------------------------------------------------------------------------

/** Sends a message that failed again; the attachment is still in storage (Phase 87). */
export function retryMessage(conversationId: string, messageId: string): Promise<MessageView> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/messages/${messageId}/retry`, {
    method: 'POST',
  });
}

export interface ChannelView {
  id: string;
  kind: ChannelKind;
  name: string;
  provider: string;
  address: string | null;
  status: 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'ERROR' | 'DISABLED';
  statusDetail: string | null;
  isDefault: boolean;
  /** The agent answering this channel, or null. */
  botAgentId: string | null;
  /** The provider's own identifier for the account — a WhatsApp phone number id. Not a secret. */
  externalId: string | null;
  /** The settings that are not credentials. */
  config: Record<string, unknown>;
  /**
   * Which credentials are set, by name — never their values.
   *
   * It is what lets a form say "there is already an access token here" without being able to read
   * one: the alternative is an empty field that wipes the credential on save, or a screen that
   * hands a permanent token to anybody who can open it.
   */
  secretsSet: string[];
  /**
   * What the WhatsApp session behind this channel is actually doing (Phase 108).
   *
   * Only the unofficial route has one. `health` is the honest word: the row saying CONNECTED is a
   * claim, HEALTHY means the engine answered recently.
   */
  session?: {
    status:
      | 'DISCONNECTED'
      | 'PAIRING'
      | 'CONNECTED'
      | 'RECONNECTING'
      | 'LOGGED_OUT'
      | 'BANNED'
      | 'FAILED';
    health: 'HEALTHY' | 'PAIRING' | 'STALE' | 'DOWN' | 'BLOCKED';
    /** Which half of pairing, when it is pairing at all. */
    stage: 'AWAITING_SCAN' | 'SYNCING' | null;
    phoneNumber: string | null;
    lastSeenAt: string | null;
    statusDetail: string | null;
  };
  createdAt: string;
}

export async function listChannels(): Promise<{ channels: ChannelView[] }> {
  return apiRequest('/v1/inbox/channels');
}

export interface ConnectChannelInput {
  kind: ChannelKind;
  name: string;
  provider?: string;
  externalId?: string | null;
  address?: string | null;
  config?: Record<string, unknown>;
  isDefault?: boolean;
}

/**
 * Connects a channel.
 *
 * The webhook secret comes back **exactly once**, here. It is the token the tenant pastes into
 * Meta's dashboard as the verify token, and nothing can read it back afterwards — so the screen
 * that receives it is the screen that has to show it.
 */
export async function connectChannel(
  input: ConnectChannelInput,
): Promise<ChannelView & { webhookSecret: string }> {
  return apiRequest('/v1/inbox/channels', { method: 'POST', body: input });
}

/**
 * Changes a connected channel.
 *
 * The config is merged and a secret sent empty keeps the one that is there, so a form can show
 * every field while knowing the value of only some.
 */
export async function updateChannel(
  channelId: string,
  input: Omit<ConnectChannelInput, 'kind' | 'provider'>,
): Promise<ChannelView> {
  return apiRequest(`/v1/inbox/channels/${channelId}`, { method: 'PATCH', body: input });
}

export async function setChannelBot(
  channelId: string,
  agentId: string | null,
): Promise<ChannelView> {
  return apiRequest(`/v1/inbox/channels/${channelId}/bot`, {
    method: 'POST',
    body: { agentId },
  });
}

export async function setConversationHandling(
  conversationId: string,
  handling: 'BOT' | 'HUMAN',
): Promise<ConversationSummary> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/handling`, {
    method: 'POST',
    body: { handling },
  });
}

export interface QueueView {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  strategy: 'MANUAL' | 'ROUND_ROBIN' | 'LEAST_BUSY';
  first_response_minutes: number | null;
  resolution_minutes: number | null;
  business_hours_only: boolean;
  is_default: boolean;
}

export async function listQueues(): Promise<{ queues: QueueView[] }> {
  return apiRequest('/v1/inbox/queues');
}

// ---------------------------------------------------------------------------
// WhatsApp

export interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  text?: string;
}

export interface TemplateView {
  id: string;
  channel_id: string;
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: string;
  status_detail: string | null;
  components: TemplateComponent[];
  parameters: Record<string, number>;
  quality: string | null;
  external_id: string | null;
  synced_at: string | null;
  created_at: string;
}

export async function listTemplates(channelId?: string): Promise<{ data: TemplateView[] }> {
  const query = channelId ? `?channelId=${encodeURIComponent(channelId)}` : '';

  return apiRequest(`/v1/whatsapp/templates${query}`);
}

export interface SessionWindow {
  open: boolean;
  minutes_left: number | null;
  last_inbound_at: string | null;
  templates: {
    id: string;
    name: string;
    language: string;
    category: string;
    components: TemplateComponent[];
  }[];
}

/**
 * Whether a free-form reply will go through on this conversation.
 *
 * Only meaningful on WhatsApp; other channels have no window, and the endpoint answers for them the
 * same way it answers for a conversation whose customer never wrote.
 */
export async function getSessionWindow(conversationId: string): Promise<SessionWindow> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/window`);
}

export async function sendTemplate(
  conversationId: string,
  input: { templateId: string; values: Record<string, string[]> },
): Promise<{ message_id: string; body: string }> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/template`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Reply buttons or a list, into an open conversation.
 *
 * Only inside the customer's own window: there is no approved-template equivalent for an
 * interactive message, so the composer hides the whole affordance once the window has closed rather
 * than letting somebody write three options for a message that cannot be sent.
 */
export async function sendInteractive(
  conversationId: string,
  message: InteractiveMessage,
): Promise<{ message_id: string; body: string }> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/interactive`, {
    method: 'POST',
    body: message,
  });
}

/**
 * Moves a conversation to a queue, and optionally to somebody on it (Phase 139).
 *
 * Leaving `assigneeId` out lets the queue's own strategy pick — round robin, least busy — which is
 * the difference between "passing this to support" and "passing this to Ana".
 */
export async function handoffConversation(
  id: string,
  input: { queueId: string; assigneeId?: string | null; note?: string },
): Promise<ConversationSummary> {
  return apiRequest(`/v1/inbox/conversations/${id}/handoff`, { method: 'POST', body: input });
}

// ----- when a channel is attended (Phase 139) ------------------------------

export interface ServiceHours {
  startMinute: number;
  endMinute: number;
  days: number[];
  timeZone: string;
}

export interface ChannelServiceView {
  serviceHours: ServiceHours | null;
  tenantHours: ServiceHours;
  awayMessage: string | null;
  waitingMessage: string | null;
  waitingAfterMinutes: number | null;
}

export function getChannelService(id: string): Promise<ChannelServiceView> {
  return apiRequest(`/v1/inbox/channels/${id}/service`);
}

export function setChannelService(
  id: string,
  input: {
    serviceHours?: ServiceHours | null;
    awayMessage?: string | null;
    waitingMessage?: string | null;
    waitingAfterMinutes?: number | null;
  },
): Promise<ChannelServiceView> {
  return apiRequest(`/v1/inbox/channels/${id}/service`, { method: 'PUT', body: input });
}

// ---------------------------------------------------------------------------
// Help with what to type (Phase 146)
// ---------------------------------------------------------------------------

export interface Suggestion {
  text: string;
  /** The articles it was allowed to rely on, so a claim can be checked before it is sent. */
  sources: { id: string; title: string }[];
  messagesUsed: number;
}

export function suggestReply(conversationId: string, messages: number): Promise<Suggestion> {
  return apiRequest(`/v1/inbox/conversations/${conversationId}/suggest`, {
    method: 'POST',
    body: { messages },
  });
}

export type PolishMode = 'SPELLING' | 'CLARITY' | 'ARGUMENTATIVE';

/** Corrects or rewrites a draft. Never sends it — what comes back lands back in the box. */
export function polishDraft(text: string, mode: PolishMode): Promise<{ text: string }> {
  return apiRequest('/v1/inbox/compose/polish', { method: 'POST', body: { text, mode } });
}
